import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ParticleBackground } from "@/components/ParticleBackground";
import { useCart } from "@/contexts/CartContext";
import { useToast } from "@/hooks/use-toast";
import Page from "@/components/layout/Page";
import { getProductPrice } from "@/utils/productHelpers";
import { SEOHead } from "@/components/seo/SEOHead";
import { BreadcrumbSchema } from "@/components/seo/StructuredData";

export default function LibrarySquares() {
  const { addItem } = useCart();
  const { toast } = useToast();
  
  const productPrice = getProductPrice("Library Squares");

  const handleAddToCart = () => {
    addItem({
      id: "library-squares",
      name: "Library Squares",
      slug: "/library-squares",
      price: productPrice,
    });
    toast({
      title: "Added to cart",
      description: "Library Squares has been added to your cart.",
    });
  };

  return (
    <>
      <SEOHead
        title="Library Squares | Tactile Sensory Tools for Calm & Focus | Samboards"
        description="Handmade tactile sensory Library Squares with nature textures. Reduce anxiety, improve focus & motor skills. Perfect for ADHD, autism, SEN classrooms. UK made."
        canonical="/library-squares"
        keywords="library squares, tactile sensory tools, sensory squares, ADHD focus tools, autism sensory tools, SEN classroom resources, anxiety relief tools, motor skills development, handmade UK, nature textures"
      />
      <BreadcrumbSchema
        items={[
          { name: "Home", url: "/" },
          { name: "Shop", url: "/shop" },
          { name: "Library Squares", url: "/library-squares" }
        ]}
      />
      
      <div className="min-h-screen overflow-hidden bg-gradient-to-b from-[#020817] via-[#04151F] to-[#020817] text-slate-50">
        <ParticleBackground className="absolute inset-0 -z-10" />

        <Page>
          <section className="pb-24">
            <div className="max-w-4xl mx-auto">
              <h1 className="font-heading text-[40px] sm:text-[48px] lg:text-[56px] leading-[1.05] page-title-gradient mb-12">
                Library Squares
              </h1>

              <div className="space-y-12">
                {/* Using Sam Library Squares */}
                <div className="rounded-3xl bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl border border-white/10 p-8 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
                  <h2 className="text-2xl sm:text-3xl font-heading bg-gradient-to-r from-emerald-200 via-teal-200 to-sky-300 bg-clip-text text-transparent mb-6">
                    Using Sam Library Squares
                  </h2>
                  <p className="text-base text-slate-200/80 leading-relaxed">
                    Use your square to decorate your phone case, stick on a surface such as a keyboard or desktop or hang on a lanyard to keep with you for easy access as you move through your day. You can make your Sam library more personal to you by tying short pieces (to avoid any possible tangles or strangulation) of your favourite string, fabric, cotton, metal ring or carabiner. You may like to use just one square, two Sam library squares together, or you may want to keep collecting them to use in different places - the choice is yours.
                  </p>
                </div>

                {/* How different textures work */}
                <div className="rounded-3xl bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl border border-white/10 p-8 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
                  <h2 className="text-2xl sm:text-3xl font-heading bg-gradient-to-r from-emerald-200 via-teal-200 to-sky-300 bg-clip-text text-transparent mb-6">
                    How different textures work with your sense of touch
                  </h2>
                  <p className="text-base text-slate-200/80 leading-relaxed mb-4">
                    Sam Library Squares include a texture from nature, a repeating pattern and a unicorn with added texture.
                  </p>
                  <p className="text-base text-slate-200/80 leading-relaxed">
                    Move your fingers around the textures on the library and think about how they feel. The library can be used for enjoyment or as a distraction while increasing messages to your brain and exercising your hands. The textures all feel different. Each texture is experienced through touch, mostly through the fingers but it can also work if a tool is used in the mouth or by using other areas of your skin. When the texture is felt, different feedback or feelings are created, the blood flows to the outside of the brain increases. By touching the library square, and finding new textures to explore, the body is releasing dopamine, which is a "feel good, stimulating" hormone. The act of touch also sends messages through the nervous system which moves blood in the brain away from the area which produces anxiety and stress. As the blood flows away, feelings of stress and anxiety may be reduced, helping you to feel calmer.
                  </p>
                </div>

                {/* What difference can the library squares make */}
                <div className="rounded-3xl bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl border border-white/10 p-8 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
                  <h3 className="text-xl font-semibold text-slate-100 mb-4">
                    What difference can the library squares make?
                  </h3>
                  <p className="text-base text-slate-200/80 leading-relaxed">
                    People using the library squares have reported a range of different benefits. For example, while touching the sensory library squares, people may feel calmer and more in control of their emotions. They may also feel more engaged and interested in the world around them. Using your hands and fingers to touch the different textures helps to create positive feelings which can improve your mood and range of movement. This can, in turn, lead to better hand control and improving motor skills for other tasks such as doing up buttons or handwriting.
                  </p>
                </div>

                {/* How long can I use */}
                <div className="rounded-3xl bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl border border-white/10 p-8 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
                  <h3 className="text-xl font-semibold text-slate-100 mb-4">
                    How long can I use my library squares for?
                  </h3>
                  <p className="text-base text-slate-200/80 leading-relaxed">
                    There is no recommended time span for the libraries to be used or how often. But if you like the feeling it gives you, keep it handy and use it whenever you want. You might find that different textures are good in different moods. Experiment and find what you like the best.
                  </p>
                </div>

                {/* Care Instructions */}
                <div className="rounded-3xl bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl border border-white/10 p-8 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
                  <h3 className="text-xl font-semibold text-slate-100 mb-4">
                    How to take care of your Sam Library
                  </h3>
                  <p className="text-base text-slate-200/80 leading-relaxed">
                    The library pieces are made to a food-safe standard and can be cleaned with hot soapy water. Please see our website for more information or to contact us with any questions. They are easy to clean with hot soapy water or cleaning sprays and rinsed if they become dirty. Alternatively, they are safe to clean in a dishwasher cutlery tray.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="w-full py-20 relative z-10">
            <div className="max-w-6xl mx-auto px-6 lg:px-8">
              <div className="rounded-3xl bg-gradient-to-br from-slate-800/40 to-slate-900/60 backdrop-blur-xl border border-white/10 p-12 text-center shadow-[0_8px_32px_rgba(0,0,0,0.4)]">
                <h2 className="mb-6 text-3xl sm:text-4xl font-heading bg-gradient-to-r from-emerald-200 via-teal-200 to-sky-300 bg-clip-text text-transparent">
                  Ready to try Library Squares?
                </h2>
                <p className="mb-8 text-base text-slate-300/70 max-w-2xl mx-auto">
                  Get in touch to learn more about our products and how they can support sensory regulation and well-being.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Button asChild size="lg" className="bg-gradient-to-r from-emerald-400 via-emerald-500 to-teal-500 text-slate-950 font-semibold shadow-[0_18px_55px_rgba(16,185,129,0.45)] hover:shadow-[0_22px_70px_rgba(16,185,129,0.6)] hover:translate-y-[-1px] transition-transform">
                    <Link to="/checkout?product=Library%20Squares">Buy now - {productPrice}</Link>
                  </Button>
                  <Button onClick={handleAddToCart} size="lg" variant="outline" className="border-2 border-emerald-300/50 text-emerald-300 hover:bg-emerald-300/10">
                    Add to cart
                  </Button>
                  <Button asChild variant="outline" size="lg" className="border-2 border-emerald-300/50 text-emerald-300 hover:bg-emerald-300/10">
                    <Link to="/contact">Contact us</Link>
                  </Button>
                </div>
              </div>
            </div>
          </section>
        </Page>
      </div>
    </>
  );
}
